import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Heart, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { finishGoogleLogin } from '@/utils/authUtils';
import Dashboard from './Dashboard';

const Callback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing authentication...');
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent multiple executions
    if (hasProcessed.current) {
      return;
    }
    hasProcessed.current = true;

    const handleCallback = async () => {
      try {
        console.log('[Callback] Handling OAuth callback (initial)');

        // Native app: server redirected to /login/callback#bearer_token=... (no script, just fragment)
        const hash = window.location.hash?.slice(1) || '';
        const hashParams = new URLSearchParams(hash);
        const bearerFromHash = hashParams.get('bearer_token');
        if (bearerFromHash) {
          localStorage.setItem('bearer_token', bearerFromHash);
          login();
          setStatus('success');
          setMessage('Authentication successful! Loading your dashboard...');
          navigate('/dashboard', { replace: true });
          return;
        }

        // Check for token parameter (from server redirect after successful token exchange)
        // This is a Google Drive OAuth token, not the app login token
        const tokenParam = searchParams.get('token');
        if (tokenParam) {
          console.log('[Callback] Found Google Drive token parameter in URL');
          try {
            // Token is double-encoded JSON: first decode the URL encoding, then parse JSON
            const decodedToken = decodeURIComponent(tokenParam);
            const tokenData = JSON.parse(decodedToken);
            
            if (tokenData.access_token) {
              console.log('[Callback] Parsed Google Drive token data, storing in localStorage');
              // Store token in localStorage for mobileGoogleDriveAuth polling to pick up
              // This is google_drive_oauth_token, NOT bearer_token (app login)
              localStorage.setItem('google_drive_oauth_token', JSON.stringify({
                access_token: tokenData.access_token,
                expires_in: tokenData.expires_in || 3600
              }));
              console.log('[Callback] Google Drive token stored in localStorage as google_drive_oauth_token');
              
              // Web flow - close popup if this is a popup window
              if (window.opener) {
                // This is a popup - delay closing for debugging
                console.log('[Callback] Popup window detected - will close in 30 seconds for debugging');
                console.log('[Callback] Token stored in localStorage:', localStorage.getItem('google_drive_oauth_token'));
                setTimeout(() => {
                  console.log('[Callback] Closing popup window now');
                  window.close();
                }, 30000); // 30 second delay for debugging
                return;
              }
              
              // Not a popup - show success and navigate
              // DO NOT call login() - this is Google Drive OAuth, not app login
              setStatus('success');
              setMessage('Google Drive connected successfully!');
              setTimeout(() => {
                navigate('/dashboard');
              }, 1500);
              return;
            } else {
              console.error('[Callback] Token data missing access_token:', tokenData);
              throw new Error('Invalid token data: missing access_token');
            }
          } catch (parseError) {
            console.error('[Callback] Failed to parse token parameter:', parseError);
            setStatus('error');
            setMessage('Failed to parse authentication token');
            return;
          }
        }

        // Check for OAuth errors first (from Google OAuth redirects)
        const error = searchParams.get('error');
        const errorSubtype = searchParams.get('error_subtype');
        const errorDescription = searchParams.get('error_description');
        
        if (error) {
          console.error('[Callback] OAuth error received:', { error, errorSubtype, errorDescription });
          setStatus('error');
          
          // Handle specific OAuth errors
          let errorMessage = 'Authentication failed';
          
          // Handle combination of error and error_subtype
          if (error === 'interaction_required' && errorSubtype === 'access_denied') {
            errorMessage = 'Access was denied. Please grant the required permissions when prompted.';
          } else if (error === 'interaction_required') {
            errorMessage = 'Please complete the authentication in the popup window and grant the required permissions.';
          } else if (error === 'access_denied') {
            errorMessage = 'Authentication was cancelled or access was denied. Please try again and grant the required permissions.';
          } else if (error === 'consent_required') {
            errorMessage = 'Please grant the required permissions to continue.';
          } else if (errorSubtype) {
            // Use error_subtype for more specific messaging
            if (errorSubtype === 'access_denied') {
              errorMessage = 'Access was denied. Please grant the required permissions.';
            } else {
              errorMessage = `Authentication error: ${error} (${errorSubtype})`;
            }
          } else if (errorDescription) {
            errorMessage = decodeURIComponent(errorDescription);
          } else {
            errorMessage = `Authentication error: ${error}`;
          }
          
          setMessage(errorMessage);
          
          // If this is a popup, close it after a short delay
          if (window.opener) {
            // Store error in localStorage so parent window can detect it
            localStorage.setItem('google_drive_oauth_error', error);
            if (errorSubtype) {
              localStorage.setItem('google_drive_oauth_error_subtype', errorSubtype);
            }
            setTimeout(() => window.close(), 3000);
          }
          
          return;
        }

        // Check if this is Google Sign-In (app login) or Google Drive OAuth
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const bearerToken = localStorage.getItem('bearer_token');
        
        // If we have code and state and no bearer yet, this is Google Sign-In (app login)
        if (code && state && !bearerToken) {
          console.log('[Callback] Handling Google Sign-In callback');
          try {
            const redirectUri = sessionStorage.getItem('google_login_redirect_uri') || `${window.location.origin}/login/callback`;
            await finishGoogleLogin(code, redirectUri);
            login();
            setStatus('success');
            setMessage('Authentication successful! Loading your dashboard...');
            const qsRedirect = searchParams.get('post_login_redirect');
            const storedRedirect = sessionStorage.getItem('post_login_redirect');
            const target = qsRedirect || storedRedirect || '/dashboard';
            if (storedRedirect) sessionStorage.removeItem('post_login_redirect');
            navigate(target, { replace: true });
            return;
          } catch (error) {
            console.error('[Callback] Google Sign-In error:', error);
            setStatus('error');
            setMessage(error instanceof Error ? error.message : 'Sign-in failed');
            return;
          }
        }
        
        // If we have code/state and already have bearer token, this is Google Drive OAuth (web)
        if (code && state && bearerToken) {
          console.log('[Callback] Handling Google Drive OAuth callback (web flow)');
          
          try {

            // Exchange code for token via backend
            const response = await fetch('/callback', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${bearerToken}`
              },
              body: JSON.stringify({ code, state })
            });

            if (!response.ok) {
              let errorText = '';
              let errorData = null;
              try {
                errorText = await response.text();
                // Try to parse as JSON to get the actual error message
                try {
                  errorData = JSON.parse(errorText);
                } catch (e) {
                  // Not JSON, use as-is
                }
              } catch (e) {
                errorText = `HTTP ${response.status}`;
              }
              
              const errorMessage = errorData?.error || errorText || `Token exchange failed: ${response.status}`;
              console.error('[Callback] Token exchange failed:', {
                status: response.status,
                error: errorMessage,
                errorData
              });
              throw new Error(errorMessage);
            }

            const result = await response.json();
            if (result.access_token) {
              // Store token in localStorage for mobileGoogleDriveAuth polling to pick up
              localStorage.setItem('google_drive_oauth_token', JSON.stringify({
                access_token: result.access_token,
                expires_in: result.expires_in
              }));
              console.log('[Callback] Google Drive token stored in localStorage for polling');
              
              // Web flow - close popup if this is a popup window
              if (window.opener) {
                // This is a popup - delay closing for debugging
                console.log('[Callback] Popup window detected (code/state flow) - will close in 30 seconds for debugging');
                console.log('[Callback] Token stored in localStorage:', localStorage.getItem('google_drive_oauth_token'));
                setTimeout(() => {
                  console.log('[Callback] Closing popup window now');
                  window.close();
                }, 30000); // 30 second delay for debugging
                return;
              }
              
              // Not a popup - show success and navigate
              setStatus('success');
              setMessage('Google Drive connected successfully!');
              setTimeout(() => {
                navigate('/dashboard');
              }, 1500);
              return;
            } else {
              throw new Error('No access token in response');
            }
          } catch (error) {
            console.error('[Callback] Google Drive OAuth error:', error);
            setStatus('error');
            setMessage(error instanceof Error ? error.message : 'Failed to complete Google Drive authentication');
            return;
          }
        }

        // No code/token - user may have landed here by mistake or after session expired
        setStatus('error');
        setMessage('No authentication data received. Please sign in again from the login page.');

      } catch (error) {
        console.error('Callback error:', error);
        setStatus('error');
        setMessage('An error occurred during authentication');
      }
    };

    handleCallback();
  }, [navigate, login]);

  if (status === 'success') {
    return <Dashboard />;
  }

  const isProcessing = status === 'processing';
  const isError = status === 'error';

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Heart className="h-12 w-12 text-primary" />
          </div>
          <CardTitle>healthscores.ai</CardTitle>
          <CardDescription>
            {isProcessing && 'Completing sign in...'}
            {isError && 'Authentication Error'}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            {isProcessing && <Loader2 className="h-4 w-4 animate-spin" />}
            <span className={
              isError ? 'text-destructive' : 'text-muted-foreground'
            }>
              {message}
            </span>
          </div>
          
          {isError && (
            <div className="mt-4">
              <button 
                onClick={() => navigate('/')}
                className="text-primary hover:underline"
              >
                Return to homepage
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Callback;
